/**
 * Digital Role - Format Pillar
 *
 * Formatação de valores de armazenamento digital para exibição.
 *
 * @example
 * import { formatDigital, getDigitalSymbol, getDigitalName } from '@attrx/role-morphic/digital/format';
 *
 * formatDigital('gigabyte', 2.5);                     // "2.5 GB"
 * formatDigital('gigabyte', 2.5, { verbose: true });  // "2.5 gigabytes"
 * getDigitalSymbol('mebibyte');                       // "MiB"
 * getDigitalName('byte', 2);                          // "bytes"
 */

import { DIGITAL_UNITS, type DigitalUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface DigitalFormatOptions {
  /** Número de casas decimais */
  decimals?: number;
  /** Usar nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Locale para formatação numérica */
  locale?: string;
  /** Notação: standard, scientific, compact */
  notation?: "standard" | "scientific" | "compact";
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor de armazenamento digital para exibição
 *
 * @param variant - Unidade
 * @param value - Valor numérico
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatDigital('gigabyte', 2.5);                     // "2.5 GB"
 * formatDigital('gigabyte', 2.5, { verbose: true });  // "2.5 gigabytes"
 * formatDigital('byte', 1, { verbose: true });        // "1 byte"
 * formatDigital('mebibyte', 100, { decimals: 0 });    // "100 MiB"
 */
export function formatDigital(
  variant: DigitalUnit,
  value: number,
  options: DigitalFormatOptions = {},
): string {
  const config = DIGITAL_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown digital unit: ${variant}`);
  }

  const { decimals, verbose = false, locale, notation = "standard" } = options;

  // Format number
  let formattedNumber: string;

  if (locale || notation !== "standard") {
    const intlOptions: Intl.NumberFormatOptions = { notation };
    if (decimals !== undefined) {
      intlOptions.minimumFractionDigits = decimals;
      intlOptions.maximumFractionDigits = decimals;
    }
    formattedNumber = new Intl.NumberFormat(locale, intlOptions).format(value);
  } else if (decimals !== undefined) {
    formattedNumber = value.toFixed(decimals);
  } else {
    // Smart formatting: remove trailing zeros
    formattedNumber = Number(value.toPrecision(12)).toString();
  }

  // Get unit label
  const unitLabel = verbose
    ? value === 1
      ? config.singular
      : config.plural
    : config.symbol;

  return `${formattedNumber} ${unitLabel}`;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Retorna o símbolo de uma unidade
 *
 * @example
 * getDigitalSymbol('megabyte');  // "MB"
 * getDigitalSymbol('mebibyte');  // "MiB"
 */
export function getDigitalSymbol(variant: DigitalUnit): string {
  const config = DIGITAL_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown digital unit: ${variant}`);
  }
  return config.symbol;
}

/**
 * Retorna o nome de uma unidade (singular ou plural)
 *
 * @param variant - Unidade
 * @param count - Quantidade para determinar singular/plural (default: 1)
 *
 * @example
 * getDigitalName('byte', 1);  // "byte"
 * getDigitalName('byte', 2);  // "bytes"
 */
export function getDigitalName(variant: DigitalUnit, count: number = 1): string {
  const config = DIGITAL_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown digital unit: ${variant}`);
  }
  return count === 1 ? (config.singular || config.symbol) : (config.plural || config.symbol);
}
